import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  IRealEstateDecision,
  IRealEstateMatchResult,
} from '../../Interfaces/IDialogInbox';
import { useSearchRealEstates } from '../../hooks/reactQuerys/DialogInboxQueries';
import { useSearchPostcodes } from '../../hooks/reactQuerys/PostcodeQueries';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { IPostcode } from '../../Interfaces/IPostcode';
import { ACTION_LABEL_REAL_ESTATE, labelForField } from '../i18n';

const normCity = (s?: string | null) =>
  (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

interface DialogRealEstate {
  Price?: number;
  LandRegister?: string;
  SheetLandRegister?: string;
  idCourt_hint?: string;
  Area?: number;
  isBuild?: boolean;
  address?: {
    raw?: string;
    Street?: string;
    StreetNr?: string;
    Postcode?: string;
    City?: string;
  };
  _objectType?: string;
  _usage?: string;
}

interface Props {
  idDialogInbox: number;
  realEstate: DialogRealEstate;
  decision?: IRealEstateDecision;
  onDecide: (decision: IRealEstateDecision) => void;
  /** Setzt die getroffene Entscheidung (inkl. „Ignoriert") wieder zurück —
   *  analog zum Mandanten (Beteiligten-Liste). */
  onReset?: () => void;
}

const MERGE_FIELDS: {
  key: string;
  label: string;
  source: 'top' | 'address';
}[] = [
  { key: 'LandRegister', label: 'Grundbuch von', source: 'top' },
  { key: 'SheetLandRegister', label: 'Grundbuchblatt', source: 'top' },
  { key: 'Street', label: 'Straße', source: 'address' },
  { key: 'StreetNr', label: 'Hausnummer', source: 'address' },
  { key: 'Area', label: 'Fläche', source: 'top' },
  { key: 'Price', label: 'Preis', source: 'top' },
];

const summarizeRealEstate = (re: DialogRealEstate): string => {
  const parts: string[] = [];
  if (re.address?.Street || re.address?.StreetNr) {
    parts.push(
      `${re.address.Street ?? ''} ${re.address.StreetNr ?? ''}`.trim()
    );
  }
  if (re.address?.Postcode || re.address?.City) {
    parts.push(`${re.address.Postcode ?? ''} ${re.address.City ?? ''}`.trim());
  }
  if (re.LandRegister) {
    parts.push(`GB ${re.LandRegister} ${re.SheetLandRegister ?? ''}`.trim());
  }
  return parts.filter(Boolean).join(' · ') || '(ohne Adresse)';
};

const summarizeMatch = (m: IRealEstateMatchResult): string => {
  const parts: string[] = [];
  if (m.Street || m.StreetNr)
    parts.push(`${m.Street ?? ''} ${m.StreetNr ?? ''}`.trim());
  if (m.LandRegister)
    parts.push(`GB ${m.LandRegister} ${m.SheetLandRegister ?? ''}`.trim());
  return parts.filter(Boolean).join(' · ') || `idRealEstate=${m.idRealEstate}`;
};

const dialogValueFor = (
  re: DialogRealEstate,
  key: string,
  source: 'top' | 'address'
) => {
  if (source === 'address') return re.address?.[key as 'Street' | 'StreetNr'];
  return (re as Record<string, unknown>)[key];
};

const matchValueFor = (m: IRealEstateMatchResult, key: string) =>
  (m as unknown as Record<string, unknown>)[key];

export const RealEstateMatchSection: React.FC<Props> = ({
  idDialogInbox,
  realEstate,
  decision,
  onDecide,
  onReset,
}) => {
  const { searchRealEstates, isSearchingRealEstates } = useSearchRealEstates();
  const [matches, setMatches] = useState<IRealEstateMatchResult[]>([]);
  const [selectedMatch, setSelectedMatch] =
    useState<IRealEstateMatchResult | null>(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeChoice, setMergeChoice] = useState<
    Record<string, 'dialog' | 'existing'>
  >({});

  // --- PLZ-Absicherung: die vom Dialog gelieferte PLZ/Stadt eindeutig auf ein
  // idPostcode auflösen; gelingt das nicht, wählt der Mitarbeiter manuell. ---
  const dialogPlz = (realEstate.address?.Postcode ?? '').trim();
  const dialogCity = (realEstate.address?.City ?? '').trim();
  const hasDialogAddress = !!(
    realEstate.address?.Street ||
    realEstate.address?.StreetNr ||
    dialogPlz ||
    dialogCity
  );
  const [postcodeObject, setPostcodeObject] = useState<IPostcode | null>(null);
  const autoResolvedRef = useRef(false);
  const { postcodes } = useSearchPostcodes(dialogPlz);

  // Einmalig versuchen, die Dialog-PLZ automatisch aufzulösen: nur ein Treffer
  // mit exakt gleicher PLZ UND (falls vorhanden) gleicher Stadt wird vorbelegt.
  useEffect(() => {
    if (autoResolvedRef.current) return;
    if (!dialogPlz || !postcodes) return;
    autoResolvedRef.current = true;
    const exact = postcodes.filter(p => p.Postcode === dialogPlz);
    const pick = dialogCity
      ? exact.find(p => normCity(p.City) === normCity(dialogCity))
      : exact.length === 1
        ? exact[0]
        : undefined;
    if (pick) setPostcodeObject(pick);
  }, [postcodes, dialogPlz, dialogCity]);

  useEffect(() => {
    setSelectedMatch(null);
    setMergeMode(false);
    setMergeChoice({});
    setPostcodeObject(null);
    autoResolvedRef.current = false;

    searchRealEstates({
      idDialogInbox,
      query: {
        LandRegister: realEstate.LandRegister,
        SheetLandRegister: realEstate.SheetLandRegister,
        Street: realEstate.address?.Street,
        StreetNr: realEstate.address?.StreetNr,
        Postcode: realEstate.address?.Postcode,
        City: realEstate.address?.City,
      },
    })
      .then(res => setMatches(res.matches))
      .catch(() => setMatches([]));
  }, [idDialogInbox, realEstate, searchRealEstates]);

  const handleUse = () => {
    if (!selectedMatch) return;
    onDecide({ action: 'use', idRealEstate: selectedMatch.idRealEstate });
  };

  const handleCreate = () =>
    onDecide({
      action: 'create',
      idPostcode: postcodeObject?.idPostcode ?? undefined,
    });
  const handleIgnore = () => onDecide({ action: 'ignore' });

  const handleStartMerge = () => {
    if (!selectedMatch) return;
    setMergeMode(true);
    const initial: Record<string, 'dialog' | 'existing'> = {};
    for (const f of MERGE_FIELDS) {
      const dV = dialogValueFor(realEstate, f.key, f.source);
      const eV = matchValueFor(selectedMatch, f.key);
      initial[f.key] = dV && dV !== eV ? 'dialog' : 'existing';
    }
    setMergeChoice(initial);
  };

  const handleConfirmMerge = () => {
    if (!selectedMatch) return;
    const mergeData: Record<string, unknown> = {};
    for (const f of MERGE_FIELDS) {
      if (mergeChoice[f.key] === 'dialog') {
        const v = dialogValueFor(realEstate, f.key, f.source);
        if (v !== undefined && v !== null && v !== '') mergeData[f.key] = v;
      }
    }
    onDecide({
      action: 'merge',
      idRealEstate: selectedMatch.idRealEstate,
      mergeData,
    });
    setMergeMode(false);
  };

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Immobilien-Suche
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant='caption' color='text.secondary'>
            Angaben aus dem Dialog
          </Typography>
          <Typography variant='body2'>
            {summarizeRealEstate(realEstate)}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {[
              realEstate.Area && `${realEstate.Area} m²`,
              realEstate.Price && `${realEstate.Price} €`,
              realEstate._objectType,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
        <Box>
          {decision && (
            <Alert
              severity={decision.action === 'ignore' ? 'warning' : 'info'}
              action={
                onReset ? (
                  <Button color='inherit' size='small' onClick={onReset}>
                    Markierung aufheben
                  </Button>
                ) : undefined
              }
            >
              Entscheidung:{' '}
              <strong>{ACTION_LABEL_REAL_ESTATE[decision.action]}</strong>
              {decision.idRealEstate
                ? ` (Immobilie Nr. ${decision.idRealEstate})`
                : ''}
            </Alert>
          )}
        </Box>
      </Box>

      {hasDialogAddress && (
        <Box sx={{ mb: 2 }}>
          {postcodeObject ? (
            <Alert severity='success' sx={{ mb: 1 }}>
              PLZ/Ort erkannt:{' '}
              <strong>
                {postcodeObject.Postcode} {postcodeObject.City}
              </strong>
              . Bei „Neu anlegen" wird diese PLZ verwendet — bei Bedarf unten
              korrigieren.
            </Alert>
          ) : (
            <Alert severity='warning' sx={{ mb: 1 }}>
              Die PLZ/den Ort aus dem Dialog (
              <strong>
                {realEstate.address?.Postcode || '—'}{' '}
                {realEstate.address?.City || ''}
              </strong>
              ) konnte nicht eindeutig zugeordnet werden. Bitte wählen Sie die
              korrekte PLZ, bevor Sie „Neu anlegen".
            </Alert>
          )}
          <PostcodeSearch
            postcodeObject={postcodeObject}
            updatePostcodeObject={v => setPostcodeObject(v ?? null)}
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Immobiliensuche ({matches.length})
      </Typography>
      {isSearchingRealEstates ? (
        <CircularProgress size={20} />
      ) : matches.length === 0 ? (
        <Alert severity='warning'>
          Keine passenden Immobilien gefunden – bitte „Neu anlegen" wählen.
        </Alert>
      ) : (
        <Stack
          spacing={1}
          sx={{
            mb: 2,
            maxHeight: 320,
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {matches.map(m => (
            <Paper
              key={m.idRealEstate}
              variant='outlined'
              onClick={() => {
                setSelectedMatch(m);
                setMergeMode(false);
              }}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                bgcolor:
                  selectedMatch?.idRealEstate === m.idRealEstate
                    ? 'action.selected'
                    : 'background.paper',
              }}
            >
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                flexWrap='wrap'
              >
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {summarizeMatch(m)}
                </Typography>
                <Chip
                  size='small'
                  label={`Übereinstimmung ${m.score}%`}
                  color={m.score >= 70 ? 'success' : 'default'}
                />
                {m.matchedFields.map(f => (
                  <Chip
                    key={f}
                    size='small'
                    label={labelForField(f)}
                    variant='outlined'
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {mergeMode && selectedMatch && (
        <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Bitte wählen Sie pro Feld den zu übernehmenden Wert:
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              gap: 1,
              alignItems: 'center',
            }}
          >
            {MERGE_FIELDS.map(f => {
              const dV = dialogValueFor(realEstate, f.key, f.source);
              const eV = matchValueFor(selectedMatch, f.key);
              return (
                <React.Fragment key={f.key}>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {f.label}
                  </Typography>
                  <RadioGroup
                    row
                    value={mergeChoice[f.key] ?? 'existing'}
                    onChange={(_, v) =>
                      setMergeChoice(prev => ({
                        ...prev,
                        [f.key]: v as 'dialog' | 'existing',
                      }))
                    }
                  >
                    <FormControlLabel
                      value='dialog'
                      control={<Radio size='small' />}
                      label={`Aus Dialog: ${dV ?? '–'}`}
                    />
                    <FormControlLabel
                      value='existing'
                      control={<Radio size='small' />}
                      label={`Bestehend: ${eV ?? '–'}`}
                    />
                  </RadioGroup>
                </React.Fragment>
              );
            })}
          </Box>
          <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
            <Button variant='contained' onClick={handleConfirmMerge}>
              Zusammenführung bestätigen
            </Button>
            <Button onClick={() => setMergeMode(false)}>Abbrechen</Button>
          </Stack>
        </Paper>
      )}

      <Stack direction='row' spacing={1} flexWrap='wrap'>
        <Button
          variant='contained'
          color='success'
          disabled={!selectedMatch || mergeMode}
          onClick={handleUse}
        >
          {ACTION_LABEL_REAL_ESTATE.use}
        </Button>
        <Button
          variant='outlined'
          color='info'
          disabled={!selectedMatch || mergeMode}
          onClick={handleStartMerge}
        >
          {ACTION_LABEL_REAL_ESTATE.merge}
        </Button>
        <Button variant='outlined' color='success' onClick={handleCreate}>
          {ACTION_LABEL_REAL_ESTATE.create}
        </Button>
        <Button variant='outlined' color='warning' onClick={handleIgnore}>
          {ACTION_LABEL_REAL_ESTATE.ignore}
        </Button>
      </Stack>
    </Paper>
  );
};
